import type { Location, NavigateFunction } from 'react-router-dom';
import type { EmailSpecialView, EmailSidebarSelectionStored } from './emailSidebarSelectionStorage';
import {
  clearEmailListReturnContext,
  loadEmailListReturnContext,
  type EmailListPaginationSnapshot,
} from './emailListReturnStorage';

/** Where to go when leaving compose (back, discard, save draft, after send). */
export interface ComposeReturnTarget {
  pathname: string;
  search?: string;
  hash?: string;
  state?: Record<string, unknown>;
}

export type ComposeLocationState = Record<string, unknown> & {
  returnTo?: ComposeReturnTarget;
};

export type TemplateLibrarySelection =
  | { root: 'company'; folderId?: number }
  | { root: 'personal'; folderId?: number }
  | { root: 'shared'; sharedFolderId?: number };

/** Restore list UI after returning from compose or email detail. */
export type ComposeListRestoreState = {
  restoreSelectedEmailId?: number;
  restorePartitionAt?: string;
  restoreSpecialView?: EmailSpecialView;
  restoreMentionsView?: boolean;
  restoreSidebarSelection?: EmailSidebarSelectionStored;
  restoreListPagination?: EmailListPaginationSnapshot;
  restoreLibrarySelection?: TemplateLibrarySelection;
  restoreLibraryTemplateId?: number;
  fromComposeSuccess?: boolean;
};

/** Query flag marking a compose opened in its own browser tab (survives refresh, unlike location.state). */
const STANDALONE_COMPOSE_PARAM = 'win';

/**
 * Cross-tab handoff for AI "create reply" body (location.state does not travel to window.open).
 * Uses localStorage so the new tab can read it regardless of sessionStorage clone timing.
 */
const INITIAL_USER_BODY_KEY = 'ums-compose-initial-user-body';

/** Append or set `win=1` on a compose / reply / forward path. */
export function withStandaloneComposeParam(path: string): string {
  const qIndex = path.indexOf('?');
  const pathname = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const search = qIndex >= 0 ? path.slice(qIndex + 1) : '';
  const params = new URLSearchParams(search);
  params.set(STANDALONE_COMPOSE_PARAM, '1');
  const q = params.toString();
  return q ? `${pathname}?${q}` : pathname;
}

export function buildStandaloneComposeUrl(): string {
  return withStandaloneComposeParam('/emails/compose');
}

export function isStandaloneComposeTab(location: Pick<Location, 'search'>): boolean {
  return new URLSearchParams(location.search).get(STANDALONE_COMPOSE_PARAM) === '1';
}

/**
 * Open compose / reply / forward in a new browser tab. Opened via script from a user gesture so the
 * tab is script-closable (exitCompose can window.close() it) and popup blockers allow it.
 */
export function openComposeInNewTab(path: string = '/emails/compose'): void {
  if (typeof window === 'undefined') return;
  const win = window.open(withStandaloneComposeParam(path), '_blank');
  win?.focus();
}

/** Stash AI-generated body for a reply that opens in a new tab. Consumed once by Compose. */
export function stashComposeInitialUserBody(text: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(INITIAL_USER_BODY_KEY, text);
  } catch {
    // ignore quota / private mode
  }
}

/** Read and clear a previously stashed AI reply body (null if none). */
export function takeComposeInitialUserBody(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const value = localStorage.getItem(INITIAL_USER_BODY_KEY);
    if (value == null) return null;
    localStorage.removeItem(INITIAL_USER_BODY_KEY);
    return value;
  } catch {
    return null;
  }
}

export function captureComposeReturnTarget(location: Location): ComposeReturnTarget {
  return {
    pathname: location.pathname,
    search: location.search || undefined,
    hash: location.hash || undefined,
    state: (location.state as Record<string, unknown> | null) ?? undefined,
  };
}

export function composeReturnFromEmailsList(
  location: Location,
  selectedEmailId?: number | null,
  partitionAt?: string | null,
  specialView?: EmailSpecialView | null,
  sidebarSelection?: EmailSidebarSelectionStored | null,
  listPagination?: EmailListPaginationSnapshot | null,
): ComposeReturnTarget {
  const state: ComposeListRestoreState = {};
  if (selectedEmailId != null) {
    state.restoreSelectedEmailId = selectedEmailId;
    if (partitionAt) state.restorePartitionAt = partitionAt;
  }
  if (specialView) {
    state.restoreSpecialView = specialView;
  }
  if (sidebarSelection) {
    state.restoreSidebarSelection = sidebarSelection;
  }
  if (listPagination && listPagination.currentPage >= 1) {
    state.restoreListPagination = listPagination;
  }
  return {
    pathname: '/emails',
    search: location.search || undefined,
    state: Object.keys(state).length > 0 ? state : undefined,
  };
}

export function composeReturnFromEmailDetail(
  emailId: number,
  partitionAt?: string | null,
  listReturn?: ComposeReturnTarget,
): ComposeReturnTarget {
  if (listReturn) return listReturn;
  return {
    pathname: `/emails/${emailId}`,
    search: partitionAt ? `?partitionAt=${encodeURIComponent(partitionAt)}` : undefined,
  };
}

/** Leave full-page email detail and restore the emails list folder context when possible. */
export function exitEmailDetail(navigate: NavigateFunction, location: Location): void {
  const returnTo = readComposeReturnTarget(location);
  if (returnTo) {
    navigate(
      {
        pathname: returnTo.pathname,
        search: returnTo.search ?? '',
        hash: returnTo.hash ?? '',
      },
      { state: returnTo.state },
    );
    return;
  }

  const ctx = loadEmailListReturnContext();
  if (ctx) {
    clearEmailListReturnContext();
    navigate(
      {
        pathname: '/emails',
        search: ctx.search || '',
      },
      {
        state: {
          restoreSelectedEmailId: ctx.restoreSelectedEmailId ?? undefined,
          restorePartitionAt: ctx.restorePartitionAt ?? undefined,
          restoreSpecialView: ctx.restoreSpecialView ?? undefined,
          restoreMentionsView: ctx.mentionsViewActive ? true : undefined,
          restoreSidebarSelection: ctx.sidebarSelection ?? undefined,
          restoreListPagination: ctx.restoreListPagination ?? undefined,
        },
      },
    );
    return;
  }

  if (typeof window !== 'undefined' && window.history.length > 1) {
    navigate(-1);
    return;
  }

  navigate('/emails');
}

export function withComposeReturn<S extends Record<string, unknown>>(
  returnTo: ComposeReturnTarget,
  state?: S,
): S & { returnTo: ComposeReturnTarget } {
  return { ...(state ?? ({} as S)), returnTo };
}

export function readComposeReturnTarget(location: Location): ComposeReturnTarget | null {
  const state = location.state as ComposeLocationState | null;
  const returnTo = state?.returnTo;
  if (!returnTo?.pathname) return null;
  return returnTo;
}

/** Leave compose and return to the view the user came from. */
export function exitCompose(
  navigate: NavigateFunction,
  location: Location,
  options?: { state?: Record<string, unknown> },
): void {
  const patch = options?.state;
  if (typeof window !== 'undefined' && isStandaloneComposeTab(location)) {
    // Compose lives in its own tab: closing it is the "return". If the browser refuses
    // to close (e.g. user navigated here directly), fall back to the inbox.
    window.close();
    window.setTimeout(() => {
      if (!window.closed) navigate('/emails', { state: patch, replace: true });
    }, 150);
    return;
  }
  const returnTo = readComposeReturnTarget(location);
  if (returnTo) {
    navigate(
      {
        pathname: returnTo.pathname,
        search: returnTo.search ?? '',
        hash: returnTo.hash ?? '',
      },
      {
        state: { ...(returnTo.state ?? {}), ...patch },
        replace: true,
      },
    );
    return;
  }
  if (typeof window !== 'undefined' && window.history.length > 1) {
    navigate(-1);
    return;
  }
  navigate('/emails', { state: patch, replace: true });
}

export function navigateToCompose(
  navigate: NavigateFunction,
  to: string,
  state: Record<string, unknown> | undefined,
  returnTo: ComposeReturnTarget,
): void {
  navigate(to, { state: withComposeReturn(returnTo, state) });
}

/** Preserve library folder/record when opening compose from the template library. */
export function withLibraryComposeReturn(
  returnTo: ComposeReturnTarget,
  library: { selection: TemplateLibrarySelection; templateId?: number },
): ComposeReturnTarget {
  const prevState = (returnTo.state ?? {}) as ComposeListRestoreState;
  const nextState: ComposeListRestoreState = {
    ...prevState,
    restoreLibrarySelection: library.selection,
  };
  if (library.templateId != null) {
    nextState.restoreLibraryTemplateId = library.templateId;
  }
  return { ...returnTo, state: nextState };
}
